import { ref, watch, type Ref } from 'vue';
import type { BookCanvas } from '@/modules/editor/canvas/BookCanvas.class';

/**
 * 移动端横屏「排除控制条占位」偏好在 localStorage 中的 key。
 * 取值：'1' 开启，'0' 关闭；缺省 / 读失败时按关闭（false）处理。
 */
const MOBILE_CONTROLS_EXCLUSION_STORAGE_KEY = 'picture-book:mobile-controls-exclusion';

/**
 * 读取移动端横屏排除控制条偏好。
 * 默认值：false（未写入、SSR、或 localStorage 不可用时均返回 false）。
 * 开启后：横屏阅读时控制条常驻，并为画布预留/排除控制条占位（见 BookReader 的 mobile-landscape-excluded）。
 */
export const readMobileControlsExclusionPreference = () => {
  if (typeof window === 'undefined') return false;

  try {
    return window.localStorage.getItem(MOBILE_CONTROLS_EXCLUSION_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
};

export const writeMobileControlsExclusionPreference = (enabled: boolean) => {
  if (typeof window === 'undefined') return;

  try {
    window.localStorage.setItem(MOBILE_CONTROLS_EXCLUSION_STORAGE_KEY, enabled ? '1' : '0');
  } catch {
    // Keep the in-memory preference when storage is unavailable.
  }
};

interface UseReaderControlsOptions {
  getCanvas: () => BookCanvas | undefined;
  isPC: Ref<boolean>;
  isZoomed: Ref<boolean>;
  keepVisible: Ref<boolean>;
}

/**
 * 控制条可见性域：底部控制条 / 侧栏按钮的显隐、常驻模式、hover 态与缩放态同步。
 */
export const useReaderControls = (options: UseReaderControlsOptions) => {
  const { getCanvas, isPC, isZoomed, keepVisible } = options;

  const controlsVisible = ref(isPC.value || keepVisible.value);
  const controlsHovered = ref(false);

  const hideControls = () => {
    controlsHovered.value = false;
    if (isPC.value || keepVisible.value) controlsVisible.value = true;
  };

  /** 保留旧调用语义：桌面端或移动常驻模式始终显示控件。 */
  const scheduleControlsHide = () => {
    if (isPC.value || keepVisible.value) controlsVisible.value = true;
  };

  /** 唤出控制条。 */
  const showControls = () => {
    controlsVisible.value = true;
  };

  const toggleControls = () => {
    if (isPC.value || keepVisible.value) {
      showControls();
      return;
    }

    controlsVisible.value = !controlsVisible.value;
  };

  // 保留鼠标与通用场景别名，让调用点自解释。
  const showControlsForMouse = () => {
    if (isPC.value) showControls();
  };
  const showControlsForCurrentViewport = () => showControls();

  const setControlsHovered = (hovered: boolean) => {
    controlsHovered.value = hovered;

    if (hovered) {
      controlsVisible.value = true;
      return;
    }

    scheduleControlsHide();
  };

  /** 切换书本缩放模式，状态由 BookZoomController 回调同步到 isZoomed。 */
  const toggleZoom = () => {
    getCanvas()?.toggleZoom();
    showControlsForCurrentViewport();
  };

  /** 绑定缩放状态回调，使底部控制条与 Pixi 顶部缩放条保持一致。 */
  const bindZoomStateListener = () => {
    const canvas = getCanvas();
    if (!canvas) return;

    canvas.setOnZoomStateChange((active) => {
      isZoomed.value = active;
    });
    isZoomed.value = canvas.isZooming();
  };

  watch(isPC, (pc) => {
    if (pc) {
      controlsVisible.value = true;
      return;
    }

    showControls();
  });

  watch(keepVisible, (visible) => {
    if (visible) showControls();
  });

  /** 销毁书本前解绑缩放回调并复位缩放态。 */
  const resetZoom = () => {
    isZoomed.value = false;
    getCanvas()?.setOnZoomStateChange(null);
  };

  return {
    controlsVisible,
    controlsHovered,
    isZoomed,
    hideControls,
    scheduleControlsHide,
    showControlsForMouse,
    showControlsForCurrentViewport,
    toggleControls,
    setControlsHovered,
    toggleZoom,
    bindZoomStateListener,
    resetZoom
  };
};
